import { SubscriptionUpdatedEvent, ThemeEvents } from '@theme/events';
import { formatMoney } from '@theme/utilities';

/**
 * Composant d'abonnement permettant de choisir entre achat unique et abonnement.
 * Gère l'affichage des prix, la sélection de fréquence et la mise à jour du formulaire produit.
 */
class SubscriptionComponent extends HTMLElement {
  constructor() {
    super();

    this.sellingPlanInput = null;
    this.oneTimeOption = null;
    this.subscriptionOption = null;
    this.oneTimeCard = null;
    this.subscriptionCard = null;
    this.frequencySelect = null;
    this.frequencyInputs = null;
    this.frequencyContainer = null;
    this.oneTimePriceContainer = null;
    this.subscriptionPriceContainer = null;
    this.deliveryMessage = null;
    this.productForm = null;

    this.handleOneTimeChange = () => this.#onOptionChange('one_time');
    this.handleSubscriptionChange = () => this.#onOptionChange('subscription');
    this.handleFrequencyDelegate = (event) => this.#onFrequencyDelegate(event);
    this.handleCardClick = (event) => this.#onCardClick(event);
    this.handlePriceUpdate = () => this.#updateDisplayedPrices();
    this.handleVariantUpdate = (event) => this.#onVariantUpdate(event);
  }

  connectedCallback() {
    this.#initDomReferences();
    this.#bindListeners();

    document.addEventListener(ThemeEvents.variantUpdated, this.handleVariantUpdate);
    document.addEventListener(ThemeEvents.quantityBreaksUpdated, this.handlePriceUpdate);

    this.#initDefaultSelection();
    this.#schedulePriceSync();
  }

  /**
   * Resync le prix « Achat unique » après présélection QB (course entre custom elements).
   */
  #schedulePriceSync() {
    const sync = () => this.#updateDisplayedPrices();
    queueMicrotask(sync);
    requestAnimationFrame(() => {
      requestAnimationFrame(sync);
    });
    setTimeout(sync, 0);
    setTimeout(sync, 100);
  }

  disconnectedCallback() {
    this.#unbindListeners();

    document.removeEventListener(ThemeEvents.variantUpdated, this.handleVariantUpdate);
    document.removeEventListener(ThemeEvents.quantityBreaksUpdated, this.handlePriceUpdate);
  }

  /**
   * API publique : appelée aussi via onchange inline du select si besoin.
   */
  updateFromFrequencyChange() {
    this.#onFrequencyChange();
  }

  #bindListeners() {
    this.oneTimeOption?.addEventListener('change', this.handleOneTimeChange);
    this.subscriptionOption?.addEventListener('change', this.handleSubscriptionChange);
    this.oneTimeCard?.addEventListener('click', this.handleCardClick);
    this.subscriptionCard?.addEventListener('click', this.handleCardClick);
    // capture:true — le change du select doit être capté même si un parent l'intercepte
    this.addEventListener('change', this.handleFrequencyDelegate, true);
    this.frequencySelect?.addEventListener('change', this.handleFrequencyDelegate);
    this.frequencySelect?.addEventListener('input', this.handleFrequencyDelegate);
  }

  #unbindListeners() {
    this.oneTimeOption?.removeEventListener('change', this.handleOneTimeChange);
    this.subscriptionOption?.removeEventListener('change', this.handleSubscriptionChange);
    this.oneTimeCard?.removeEventListener('click', this.handleCardClick);
    this.subscriptionCard?.removeEventListener('click', this.handleCardClick);
    this.removeEventListener('change', this.handleFrequencyDelegate, true);
    this.frequencySelect?.removeEventListener('change', this.handleFrequencyDelegate);
    this.frequencySelect?.removeEventListener('input', this.handleFrequencyDelegate);
  }

  /**
   * Initialise les références DOM du composant.
   */
  #initDomReferences() {
    this.sellingPlanInput = this.querySelector('[data-ref="selling-plan-input"]');
    this.oneTimeOption = this.querySelector('[data-ref="one-time-option"]');
    this.subscriptionOption = this.querySelector('[data-ref="subscription-option"]');
    this.oneTimeCard = this.querySelector('[data-ref="one-time-card"]');
    this.subscriptionCard = this.querySelector('[data-ref="subscription-card"]');
    this.frequencySelect = this.querySelector('[data-ref="frequency-select"]');
    this.frequencyInputs = this.querySelectorAll('[data-ref="frequency-input"]');
    this.frequencyContainer = this.querySelector('[data-ref="frequency-container"]');
    this.oneTimePriceContainer = this.querySelector('[data-ref="one-time-price"]');
    this.subscriptionPriceContainer = this.querySelector('[data-ref="subscription-price"]');
    this.deliveryMessage = this.querySelector('[data-ref="delivery-message"]');
    this.productForm = this.closest('[data-ref="product-section-content"]')?.querySelector('[data-ref="product-form"]') || this.closest('[data-ref="popup-content"]')?.querySelector('[data-ref="product-form"]');
  }

  /**
   * Gère la mise à jour au changement de variante.
   * Re-rend le composant avec le HTML de la nouvelle variante.
   * @param {CustomEvent} event - L'événement de mise à jour de variante
   */
  #onVariantUpdate(event) {
    if (!event.detail?.data?.html) return;

    const newSubscription = event.detail.data.html.querySelector('subscription-component');
    const hasSubscriptionContent = newSubscription?.querySelector('[data-ref="selling-plan-input"]');

    // Si la nouvelle variante n'a pas d'abonnement, masquer le composant et vider le contenu
    if (!hasSubscriptionContent) {
      this.style.display = 'none';
      this.innerHTML = '';

      // Réinitialiser les références DOM à null pour que isSubscriptionSelected() retourne false
      this.sellingPlanInput = null;
      this.oneTimeOption = null;
      this.subscriptionOption = null;
      this.oneTimeCard = null;
      this.subscriptionCard = null;
      this.frequencySelect = null;
      this.frequencyInputs = null;
      this.frequencyContainer = null;
      this.oneTimePriceContainer = null;
      this.subscriptionPriceContainer = null;
      this.deliveryMessage = null;

      this.dispatchEvent(new SubscriptionUpdatedEvent());
      return;
    }

    // Afficher le composant et mettre à jour le contenu
    this.style.display = '';
    this.innerHTML = newSubscription.innerHTML;

    // Retirer les anciens listeners avant de réinitialiser les références
    this.#unbindListeners();

    // Réinitialiser les références DOM
    this.#initDomReferences();

    // Réattacher les listeners
    this.#bindListeners();

    // Réinitialiser la sélection par défaut
    this.#initDefaultSelection();
    this.#schedulePriceSync();
  }

  /**
   * Initialise la sélection par défaut selon le paramètre data-default-selected.
   * Si le produit nécessite un abonnement (requires_selling_plan), l'abonnement est forcé.
   */
  #initDefaultSelection() {
    const requiresSellingPlan = this.dataset.requiresSellingPlan === 'true';

    // Cas où le produit nécessite un abonnement (pas d'option achat unique)
    if (requiresSellingPlan) {
      if (this.subscriptionOption && this.isSubscriptionActivated()) {
        this.subscriptionOption.checked = true;
        this.#onOptionChange('subscription', true);
      }
      this.#enableCollapseAnimation();
      return;
    }

    // Ne rien faire si le composant est vide (variante sans abonnement)
    if (!this.oneTimeOption) {
      this.#enableCollapseAnimation();
      return;
    }

    // Forcer one_time si les quantity breaks sont en mode "variant picker" (incompatible avec abonnement)
    if (this.#isQuantityBreaksVariantPickerActive()) {
      this.oneTimeOption.checked = true;
      this.#onOptionChange('one_time', true);
      this.#enableCollapseAnimation();
      return;
    }

    const defaultSelected = this.dataset.defaultSelected || 'one_time';

    if (defaultSelected === 'subscription' && this.subscriptionOption && this.isSubscriptionActivated()) {
      this.subscriptionOption.checked = true;
      this.#onOptionChange('subscription', true);
    } else {
      this.oneTimeOption.checked = true;
      this.#onOptionChange('one_time', true);
    }

    this.#enableCollapseAnimation();
  }

  /**
   * Active les transitions collapse après le paint initial (évite l'animation au chargement).
   */
  #enableCollapseAnimation() {
    this.classList.remove('subscription--animate');
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('subscription--animate');
      });
    });
  }

  /**
   * Vérifie si les quantity breaks sont en mode "sélecteur de variante".
   * Ce mode est incompatible avec les abonnements.
   * @returns {boolean}
   */
  #isQuantityBreaksVariantPickerActive() {
    const quantityBreaks = this.#getQuantityBreaks();
    return quantityBreaks?.dataset.showVariantPicker === 'true';
  }

  /**
   * Gère le changement d'option (achat unique / abonnement).
   * @param {'one_time' | 'subscription'} optionType - Type d'option sélectionnée
   * @param {boolean} notify - Indique si l'événement de mise à jour doit être déclenché
   */
  #onOptionChange(optionType, notify = true) {
    this.#updateSelectedCard(optionType);

    if (optionType === 'subscription') {
      this.#showFrequencySelect();
      this.#updateSellingPlanInput(this.#getSelectedFrequencyValue());
      this.#updateDeliveryMessage();
    } else {
      this.#hideFrequencySelect();
      this.#updateSellingPlanInput('');
      this.#hideDeliveryMessage();
    }

    this.#updateDisplayedPrices();
    if (notify) {
      this.dispatchEvent(new SubscriptionUpdatedEvent());
    }
  }

  /**
   * Clic sur une carte (plus de <label> englobant — le select de fréquence doit rester utilisable).
   * @param {MouseEvent} event
   */
  #onCardClick(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    // Laisser les contrôles de fréquence / QB / liens gérer leur propre interaction
    if (
      target.closest(
        '[data-ref="frequency-container"], [data-ref="frequency-select"], [data-ref="frequency-input"], label[for^="frequency-"], quantity-breaks, a, button, input, select, textarea'
      )
    ) {
      // Si on touche la fréquence, forcer l'option abo sélectionnée
      if (target.closest('[data-ref="frequency-container"]') && this.subscriptionOption && !this.subscriptionOption.checked) {
        this.subscriptionOption.checked = true;
        this.#onOptionChange('subscription', false);
      }
      return;
    }

    const card = event.currentTarget;
    if (card === this.subscriptionCard && this.subscriptionOption && !this.subscriptionOption.checked) {
      this.subscriptionOption.checked = true;
      this.#onOptionChange('subscription');
    } else if (card === this.oneTimeCard && this.oneTimeOption && !this.oneTimeOption.checked) {
      this.oneTimeOption.checked = true;
      this.#onOptionChange('one_time');
    }
  }

  /**
   * Gère le changement de fréquence d'abonnement.
   */
  #onFrequencyChange() {
    this.#updateSellingPlanInput(this.#getSelectedFrequencyValue());
    this.#updateDisplayedPrices();
    this.#updateDeliveryMessage();
    this.dispatchEvent(new SubscriptionUpdatedEvent());
  }

  /**
   * @param {Event} event
   */
  #onFrequencyDelegate(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (
      target.matches('[data-ref="frequency-select"], [data-ref="frequency-input"]') ||
      target.closest('[data-ref="frequency-select"]')
    ) {
      this.#onFrequencyChange();
    }
  }

  /**
   * Met à jour les prix affichés dans les cartes one-time et abonnement.
   * Applique les réductions dans l'ordre Shopify : Abonnement → Quantity Breaks.
   * Les cross-sells sont ajoutés après les réductions (pas de réduction sur les cross-sells).
   */
  #updateDisplayedPrices() {
    // Carte achat unique : toujours le palier QB (sélectionné ou défaut), même si abo coché
    const oneTimePrices = this.#getOneTimePricesFromQuantityBreaks();
    if (oneTimePrices) {
      this.#updatePriceElement(
        this.oneTimePriceContainer,
        oneTimePrices.price,
        oneTimePrices.compareAtPrice,
        false,
      );
    } else if (!this.#getQuantityBreaks()) {
      // Pas de QB → prix variante. Si QB présent mais pas encore prêt : ne pas écraser le SSR.
      const { priceAfterQB, compareAtPrice, crossSellPrice, crossSellCompareAtPrice } = this.#getBasePrices();
      if (priceAfterQB != null) {
        this.#updatePriceElement(
          this.oneTimePriceContainer,
          priceAfterQB + crossSellPrice,
          compareAtPrice + crossSellCompareAtPrice,
          false,
        );
      }
    }

    // Carte abonnement : prix catalogue (unitaire × qty) barré, puis −% selling plan
    const subscriptionLine = this.getSubscriptionLinePrice();
    this.#updatePriceElement(
      this.subscriptionPriceContainer,
      subscriptionLine.price,
      subscriptionLine.compareAtPrice,
      true,
    );
  }

  /**
   * Prix du palier QB (sélectionné, sinon index data-quantity-selected-by-default).
   * Lit le dataset HTML — pas besoin que le custom element soit upgradé ni --selected.
   * @returns {{ price: number, compareAtPrice: number } | null}
   */
  #getOneTimePricesFromQuantityBreaks() {
    const quantityBreaks = this.#getQuantityBreaks();
    if (!quantityBreaks) return null;

    const items = quantityBreaks.querySelectorAll('[data-ref="quantity-break-item"]');
    if (!items.length) return null;

    const defaultIndex = parseInt(quantityBreaks.dataset.quantitySelectedByDefault, 10) || 1;
    const selected =
      quantityBreaks.querySelector('.quantity-break--selected') || items[Math.max(0, defaultIndex - 1)] || items[0];

    const price = parseInt(selected.dataset.totalPrice, 10);
    if (Number.isNaN(price)) return null;

    return {
      price,
      compareAtPrice: parseInt(selected.dataset.totalCompareAtPrice, 10) || price,
    };
  }

  /**
   * Prix ligne abonnement : (prix unitaire × qty) − réduction selling plan.
   * Ex. 6 × 25 € = 150 € barré → −10 % = 135 €.
   * @returns {{ price: number, compareAtPrice: number, quantity: number }}
   */
  getSubscriptionLinePrice() {
    const subscriptionQty = this.#getPlanProductQuantity();
    const addToCartContainer = this.productForm?.querySelector('[data-ref="add-to-cart-button-container"]');
    const unitPrice = parseInt(addToCartContainer?.dataset.price) || 0;
    const unitCompareAt = parseInt(addToCartContainer?.dataset.compareAtPrice) || unitPrice;

    const subscriptionCompareAt = unitCompareAt * subscriptionQty;
    const subscriptionBase = unitPrice * subscriptionQty;

    return {
      price: Math.max(0, this.#calculateSubscriptionPrice(subscriptionBase, subscriptionQty)),
      compareAtPrice: subscriptionCompareAt,
      quantity: subscriptionQty,
    };
  }

  /**
   * Récupère les prix de base depuis les quantity breaks ou le product form.
   * @returns {{ priceAfterQB: number, priceBeforeQB: number, compareAtPrice: number, quantity: number, qbDiscount: object|null, crossSellPrice: number, crossSellCompareAtPrice: number }}
   */
  #getBasePrices() {
    const quantityBreaks = this.#getQuantityBreaks();

    if (quantityBreaks && typeof quantityBreaks.getPrices === 'function' && quantityBreaks.isActive?.()) {
      const prices = quantityBreaks.getPrices();
      const quantity = parseInt(quantityBreaks.getSelectedQuantity?.()?.dataset?.quantity) || 1;
      const discount = typeof quantityBreaks.getDiscount === 'function' ? quantityBreaks.getDiscount() : null;

      const priceAfterQB = parseInt(prices.price) || 0;
      const compareAtPrice = parseInt(prices.compareAtPrice) || priceAfterQB;

      // Récupérer les prix des cross-sells (séparément pour ne pas appliquer la réduction abonnement dessus)
      let crossSellPrice = 0;
      let crossSellCompareAtPrice = 0;
      if (typeof quantityBreaks.getCrossSellPrices === 'function') {
        const crossSellPrices = quantityBreaks.getCrossSellPrices();
        crossSellPrice = parseInt(crossSellPrices.price) || 0;
        crossSellCompareAtPrice = parseInt(crossSellPrices.compareAtPrice) || crossSellPrice;
      }

      // Récupérer le prix unitaire de la variante depuis le bouton d'ajout au panier
      const addToCartContainer = this.productForm?.querySelector('[data-ref="add-to-cart-button-container"]');
      const variantUnitPrice = parseInt(addToCartContainer?.dataset.price) || 0;
      const variantUnitCompareAtPrice = parseInt(addToCartContainer?.dataset.compareAtPrice) || variantUnitPrice;

      // Le prix avant réduction QB = prix unitaire de la variante × quantité
      const priceBeforeQB = variantUnitPrice * quantity;

      return {
        priceAfterQB,
        priceBeforeQB,
        compareAtPrice: variantUnitCompareAtPrice * quantity,
        quantity,
        qbDiscount: discount,
        crossSellPrice,
        crossSellCompareAtPrice,
      };
    }

    // Sans QB, récupérer le prix depuis le bouton d'ajout au panier
    const addToCartContainer = this.productForm?.querySelector('[data-ref="add-to-cart-button-container"]');
    const variantPrice = parseInt(addToCartContainer?.dataset.price) || 0;
    const variantCompareAtPrice = parseInt(addToCartContainer?.dataset.compareAtPrice) || variantPrice;

    return {
      priceAfterQB: variantPrice,
      priceBeforeQB: variantPrice,
      compareAtPrice: variantCompareAtPrice,
      quantity: 1,
      qbDiscount: null,
      crossSellPrice: 0,
      crossSellCompareAtPrice: 0,
    };
  }

  /**
   * Récupère le composant quantity breaks (embarqué dans l'abo, sinon frère dans le form).
   * @returns {HTMLElement | null}
   */
  #getQuantityBreaks() {
    return (
      this.querySelector('[data-ref="quantity-breaks"]') ||
      this.productForm?.querySelector('[data-ref="quantity-breaks"]') ||
      null
    );
  }

  /**
   * Calcule le prix avec la réduction abonnement appliquée.
   * @param {number} basePrice - Prix de base en centimes
   * @param {number} quantity - Nombre d'articles
   * @returns {number} Prix ajusté en centimes
   */
  #calculateSubscriptionPrice(basePrice, quantity = 1) {
    const adjustment = this.#getSelectedPlanAdjustment();
    return this.#applyPriceAdjustment(basePrice, adjustment, quantity);
  }

  /**
   * Récupère l'ajustement de prix du selling plan sélectionné.
   * @returns {{ type: string, value: number } | null}
   */
  #getSelectedPlanAdjustment() {
    const selectedElement = this.#getSelectedFrequencyElement();
    if (!selectedElement) return null;

    return {
      type: selectedElement.dataset.adjustmentType || 'percentage',
      value: parseFloat(selectedElement.dataset.adjustmentValue) || 0,
    };
  }

  /**
   * Applique un ajustement de prix (pourcentage, montant fixe ou prix fixe).
   * @param {number} basePrice - Prix de base en centimes
   * @param {{ type: string, value: number } | null} adjustment - Ajustement à appliquer
   * @param {number} quantity - Nombre d'articles (pour fixed_amount et price)
   * @returns {number} Prix ajusté en centimes
   */
  #applyPriceAdjustment(basePrice, adjustment, quantity = 1) {
    if (!adjustment) return basePrice;

    switch (adjustment.type) {
      case 'percentage':
        let unitBasePrice = basePrice / quantity;
        let unitDiscountAmount = (unitBasePrice * adjustment.value) / 100;
        let unitSubscriptionPrice = unitBasePrice - unitDiscountAmount;
        unitSubscriptionPrice = Math.round(unitSubscriptionPrice);
        let returnedPrice = unitSubscriptionPrice * quantity;
        return returnedPrice;
      case 'fixed_amount':
        return Math.max(0, basePrice - adjustment.value * quantity);
      case 'price':
        return adjustment.value * quantity;
      default:
        return basePrice;
    }
  }

  /**
   * Met à jour l'affichage d'un élément de prix (prix + prix barré + badge).
   * @param {HTMLElement | null} container - Conteneur du prix
   * @param {number} price - Prix à afficher en centimes
   * @param {number} compareAtPrice - Prix barré en centimes
   * @param {boolean} showSubscriptionBadge - Afficher le badge de réduction abonnement
   */
  #updatePriceElement(container, price, compareAtPrice, showSubscriptionBadge = false) {
    if (!container) return;

    const priceEl = container.querySelector('[data-ref="price"]');
    const compareAtPriceEl = container.querySelector('[data-ref="compare-at-price"]');
    const badgeEl = container.querySelector('[data-ref="difference-price-value"]');

    if (priceEl) {
      priceEl.textContent = formatMoney(price);
      priceEl.dataset.value = price;
    }

    const hasSavings = compareAtPrice > price;

    if (compareAtPriceEl) {
      if (hasSavings) {
        compareAtPriceEl.textContent = formatMoney(compareAtPrice);
        compareAtPriceEl.dataset.value = compareAtPrice;
      } else {
        compareAtPriceEl.textContent = '';
        compareAtPriceEl.dataset.value = '';
      }
    }

    if (badgeEl) {
      const badgeTextEl = container.querySelector('.badge__text');

      if (!showSubscriptionBadge) {
        if (badgeTextEl) badgeTextEl.textContent = '';
        badgeEl.textContent = '';
      } else {
        const discountValue = this.#getSubscriptionDiscountValue();
        const template = (this.dataset.salesBadgeTemplate || '').trim();

        if (template) {
          // Template custom marchand
          if (badgeTextEl) badgeTextEl.textContent = '';
          badgeEl.textContent = discountValue ? template.replace('{discount}', discountValue) : '';
        } else {
          // Comportement de base : « Économisez » + « 10% »
          if (badgeTextEl) badgeTextEl.textContent = this.dataset.salesBadgePrefix || '';
          badgeEl.textContent = discountValue;
        }
      }
    }
  }

  /**
   * Valeur brute de la réduction (ex. "10%", "5,00 €").
   * @returns {string}
   */
  #getSubscriptionDiscountValue() {
    const adjustment = this.#getSelectedPlanAdjustment();
    if (!adjustment || adjustment.value === 0) return '';

    switch (adjustment.type) {
      case 'percentage':
        return `${adjustment.value}%`;
      case 'fixed_amount':
      case 'price':
        return formatMoney(adjustment.value);
      default:
        return '';
    }
  }

  /**
   * Met à jour la classe CSS de la carte sélectionnée.
   * @param {'one_time' | 'subscription'} optionType - Type d'option sélectionnée
   */
  #updateSelectedCard(optionType) {
    this.oneTimeCard?.classList.remove('subscription__option--selected');
    this.subscriptionCard?.classList.remove('subscription__option--selected');

    if (optionType === 'one_time') {
      this.oneTimeCard?.classList.add('subscription__option--selected');
    } else {
      this.subscriptionCard?.classList.add('subscription__option--selected');
    }
  }

  #showFrequencySelect() {
    if (this.frequencyContainer) {
      this.frequencyContainer.style.display = 'block';
    }
  }

  #hideFrequencySelect() {
    if (this.frequencyContainer) {
      this.frequencyContainer.style.display = 'none';
    }
  }

  #hideDeliveryMessage() {
    if (!this.deliveryMessage) return;
    // Mode unifié : le collapse masque déjà le contenu de l'option
    if (this.classList.contains('subscription--unified')) return;
    this.deliveryMessage.hidden = true;
  }

  /**
   * Met à jour le texte "Vous recevrez X produit(s) tous les Y mois" selon le selling plan.
   * Mode match_interval : qty = intervalle. Mode always_one : toujours 1 produit.
   * La fréquence est lue depuis le libellé du plan (Recharge) en priorité — les policies
   * Liquid sont souvent à 1 mois pour tous les plans prépayés.
   */
  #updateDeliveryMessage() {
    if (!this.deliveryMessage) return;

    const selected = this.#getSelectedFrequencyElement();
    if (!selected) {
      this.#hideDeliveryMessage();
      return;
    }

    const { intervalCount, interval } = this.#resolveFrequency(selected);
    const quantityMode = this.deliveryMessage.dataset.quantityMode || 'match_interval';
    const productCount = quantityMode === 'always_one' ? 1 : intervalCount;

    const productSingular = this.deliveryMessage.dataset.productSingular || 'produit';
    const productPlural = this.deliveryMessage.dataset.productPlural || 'produits';
    const template =
      this.deliveryMessage.dataset.messageTemplate || 'Vous recevrez {quantity} tous les {frequency}';

    const quantityLabel = `${this.#formatFrenchCount(productCount)} ${productCount > 1 ? productPlural : productSingular}`;
    const frequencyLabel = `${this.#formatFrenchCount(intervalCount)} ${this.#formatIntervalUnit(interval, intervalCount)}`;

    this.deliveryMessage.textContent = template
      .replace('{quantity}', quantityLabel)
      .replace('{frequency}', frequencyLabel);
    this.deliveryMessage.hidden = false;
  }

  /**
   * Résout intervalle + count depuis le libellé visible, sinon les data-attributes.
   * @param {HTMLElement} el
   * @returns {{ intervalCount: number, interval: string }}
   */
  #resolveFrequency(el) {
    const fromLabel = this.#parseFrequencyFromText(this.#getFrequencyLabel(el));
    if (fromLabel) return fromLabel;

    const intervalCount = parseInt(el.getAttribute('data-interval-count') || el.dataset.intervalCount, 10) || 1;
    const interval = (el.getAttribute('data-interval') || el.dataset.interval || 'month').toLowerCase();
    return { intervalCount, interval };
  }

  /**
   * @param {HTMLElement} el
   * @returns {string}
   */
  #getFrequencyLabel(el) {
    const planName = el.getAttribute('data-plan-name') || el.dataset.planName;
    if (planName) return planName.trim();

    if (el instanceof HTMLOptionElement) {
      return (el.textContent || '').trim();
    }

    if (el.id) {
      const label = this.querySelector(`label[for="${CSS.escape(el.id)}"]`);
      if (label) return (label.textContent || '').trim();
    }

    return '';
  }

  /**
   * Extrait "3 mois" / "trimestriel" / "every 3 months" du nom de plan Recharge.
   * @param {string} text
   * @returns {{ intervalCount: number, interval: string } | null}
   */
  #parseFrequencyFromText(text) {
    if (!text) return null;
    const normalized = text.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

    if (/trimestr/.test(normalized)) return { intervalCount: 3, interval: 'month' };
    if (/semestr/.test(normalized)) return { intervalCount: 6, interval: 'month' };
    if (/\bannuel|yearly|annual\b/.test(normalized)) return { intervalCount: 12, interval: 'month' };
    if (/\bmensuel|monthly\b/.test(normalized)) return { intervalCount: 1, interval: 'month' };

    const wordCounts = {
      un: 1,
      une: 1,
      deux: 2,
      trois: 3,
      quatre: 4,
      cinq: 5,
      six: 6,
      sept: 7,
      huit: 8,
      neuf: 9,
      dix: 10,
      onze: 11,
      douze: 12,
    };

    const patterns = [
      { regex: /(\d+)\s*mois/, interval: 'month' },
      { regex: /(un|une|deux|trois|quatre|cinq|six|sept|huit|neuf|dix|onze|douze)\s*mois/, interval: 'month', fromWord: true },
      { regex: /(\d+)\s*months?/, interval: 'month' },
      { regex: /(\d+)\s*semaines?/, interval: 'week' },
      { regex: /(un|une|deux|trois|quatre|cinq|six|sept|huit|neuf|dix|onze|douze)\s*semaines?/, interval: 'week', fromWord: true },
      { regex: /(\d+)\s*weeks?/, interval: 'week' },
      { regex: /(\d+)\s*jours?/, interval: 'day' },
      { regex: /(\d+)\s*days?/, interval: 'day' },
      { regex: /(\d+)\s*ans?/, interval: 'year' },
      { regex: /(\d+)\s*years?/, interval: 'year' },
      { regex: /chaque\s+mois/, interval: 'month', count: 1 },
      { regex: /every\s+month/, interval: 'month', count: 1 },
      { regex: /90\s*jours?/, interval: 'month', count: 3 },
      { regex: /30\s*jours?/, interval: 'month', count: 1 },
    ];

    for (const pattern of patterns) {
      const match = normalized.match(pattern.regex);
      if (!match) continue;
      const intervalCount = pattern.count || (pattern.fromWord ? wordCounts[match[1]] : parseInt(match[1], 10)) || 1;
      return { intervalCount, interval: pattern.interval };
    }

    return null;
  }

  /**
   * @param {number} count
   * @returns {string}
   */
  #formatFrenchCount(count) {
    const words = {
      1: 'un',
      2: 'deux',
      3: 'trois',
      4: 'quatre',
      5: 'cinq',
      6: 'six',
      7: 'sept',
      8: 'huit',
      9: 'neuf',
      10: 'dix',
      11: 'onze',
      12: 'douze',
    };
    return words[count] || String(count);
  }

  /**
   * @param {string} interval
   * @param {number} count
   * @returns {string}
   */
  #formatIntervalUnit(interval, count) {
    switch (interval) {
      case 'day':
        return count > 1 ? 'jours' : 'jour';
      case 'week':
        return count > 1 ? 'semaines' : 'semaine';
      case 'year':
        return count > 1 ? 'ans' : 'an';
      case 'month':
      default:
        return 'mois';
    }
  }

  /**
   * Récupère la valeur de fréquence sélectionnée (select ou bouton).
   * @returns {string}
   */
  #getSelectedFrequencyValue() {
    if (this.frequencySelect) {
      return this.frequencySelect.value || '';
    }

    if (this.frequencyInputs?.length > 0) {
      const checkedInput = this.querySelector('[data-ref="frequency-input"]:checked');
      return checkedInput?.value || '';
    }

    return '';
  }

  /**
   * Récupère l'élément de fréquence sélectionné (option ou input).
   * @returns {HTMLElement | null}
   */
  #getSelectedFrequencyElement() {
    if (this.frequencySelect?.options?.length) {
      const index = Math.max(this.frequencySelect.selectedIndex, 0);
      return this.frequencySelect.options[index] || null;
    }

    if (this.frequencyInputs?.length > 0) {
      return this.querySelector('[data-ref="frequency-input"]:checked') || this.frequencyInputs[0];
    }

    return null;
  }

  /**
   * Met à jour la valeur de l'input hidden selling_plan.
   * @param {string} value - ID du selling plan ou chaîne vide
   */
  #updateSellingPlanInput(value) {
    if (this.sellingPlanInput) {
      this.sellingPlanInput.value = value;
    }
  }

  /**
   * Récupère l'ID du selling plan sélectionné.
   * @returns {string} ID du selling plan ou chaîne vide
   */
  getSelectedSellingPlan() {
    return this.sellingPlanInput?.value || '';
  }

  /**
   * Indique si l'option abonnement est sélectionnée.
   * @returns {boolean}
   */
  isSubscriptionSelected() {
    return this.subscriptionOption?.checked || false;
  }

  /**
   * Quantité de produits à ajouter au panier selon le plan (ex. 6 mois → 6).
   * Respecte le mode always_one du message de livraison.
   * @returns {number}
   */
  getProductQuantity() {
    if (!this.isSubscriptionSelected()) return 1;
    return this.#getPlanProductQuantity();
  }

  /**
   * Qty déduite du selling plan sélectionné (indépendamment de l'option one-time / abo cochée).
   * @returns {number}
   */
  #getPlanProductQuantity() {
    const selected = this.#getSelectedFrequencyElement();
    if (!selected) return 1;

    const { intervalCount } = this.#resolveFrequency(selected);
    const quantityMode = this.deliveryMessage?.dataset.quantityMode || 'match_interval';
    return quantityMode === 'always_one' ? 1 : Math.max(1, intervalCount);
  }

  /**
   * Indique si le composant est visible (non masqué par les quantity breaks).
   * @returns {boolean}
   */
  isSubscriptionActivated() {
    return this.style.display !== 'none';
  }

  /**
   * Récupère l'ajustement de prix du selling plan sélectionné.
   * @returns {{ type: string, value: number } | null} Null si achat unique sélectionné
   */
  getPriceAdjustment() {
    if (!this.isSubscriptionSelected()) return null;
    return this.#getSelectedPlanAdjustment();
  }

  /**
   * Calcule le prix ajusté en fonction de l'abonnement sélectionné.
   * Utilisé par product-form.js pour le calcul du prix total.
   * @param {number} basePrice - Prix de base en centimes
   * @param {number} quantity - Nombre d'articles
   * @returns {number} Prix ajusté en centimes
   */
  calculateAdjustedPrice(basePrice, quantity = 1) {
    return this.#applyPriceAdjustment(basePrice, this.getPriceAdjustment(), quantity);
  }
}

customElements.define('subscription-component', SubscriptionComponent);
